<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      agentModeTimeout: 'never',
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    timeoutOptions() {
      return [
        { value: '5min', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_5MIN') },
        { value: '10min', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_10MIN') },
        { value: '15min', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_15MIN') },
        { value: '30min', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_30MIN') },
        { value: '1hr', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_1HR') },
        { value: '4hr', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_4HR') },
        { value: '1day', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_1DAY') },
        { value: '1week', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_1WEEK') },
        { value: 'never', label: this.$t('AGENT_MODE.TIMEOUT.OPTION_NEVER') },
      ];
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    inbox: {
      immediate: true,
      handler(newInbox) {
        if (newInbox) {
          this.agentModeTimeout = newInbox.agent_mode_timeout || 'never';
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
    // Initialize agent mode timeout from inbox
    if (this.inbox?.agent_mode_timeout) {
      this.agentModeTimeout = this.inbox.agent_mode_timeout;
    }
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async updateAgentModeTimeout() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          agent_mode_timeout: this.agentModeTimeout,
        });
        useAlert(this.$t('AGENT_MODE.MESSAGES.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('AGENT_MODE.MESSAGES.UPDATE_ERROR'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <div v-else class="flex flex-col gap-8">
      <!-- Bot Selection -->
      <form class="flex flex-wrap mx-0" @submit.prevent="updateActiveAgentBot">
        <SettingsSection
          :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
          :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        >
          <div>
            <label>
              <select v-model="selectedAgentBotId">
                <option value="" disabled selected>
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="agentBot in agentBots"
                  :key="agentBot.id"
                  :value="agentBot.id"
                >
                  {{ agentBot.name }}
                </option>
              </select>
            </label>
            <div class="button-container space-x-2">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :is-loading="uiFlags.isDisconnecting"
                faded
                ruby
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </SettingsSection>
      </form>

      <!-- Agent Mode Timeout Settings -->
      <form
        class="flex flex-wrap mx-0"
        @submit.prevent="updateAgentModeTimeout"
      >
        <SettingsSection
          :title="$t('AGENT_MODE.SETTINGS.TITLE')"
          :sub-title="$t('AGENT_MODE.SETTINGS.DESCRIPTION')"
        >
          <div>
            <label class="block mb-2">
              <span class="text-sm font-medium text-n-slate-12 mb-2 block">
                {{ $t('AGENT_MODE.SETTINGS.TIMEOUT_LABEL') }}
              </span>
              <select v-model="agentModeTimeout" class="w-full">
                <option
                  v-for="option in timeoutOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <p class="text-xs text-n-slate-10 mb-4">
              {{ $t('AGENT_MODE.SETTINGS.TIMEOUT_HELP') }}
            </p>
            <div class="button-container">
              <NextButton type="submit" :label="$t('AGENT_MODE.SETTINGS.SAVE')">
                {{ $t('AGENT_MODE.SETTINGS.SAVE') }}
              </NextButton>
            </div>
          </div>
        </SettingsSection>
      </form>
    </div>
  </div>
</template>
